import { t } from "i18next";
import { StatusFile } from "@/common/enums/enums";
import { CustomCollapsibleItem } from "@/components/ui/custom-collapsible-item";
import { ParagraphSkeleton } from "@/components/ui/skeleton/ParagraphSkeleton";
import { OwnedAudioPlayer } from "@/components/ui/visits/owned-audio-player";
import { SpeakerTranscript } from "@/components/ui/visits/SpeakerTranscript";
import { useTranscriptV3 } from "@/components/ui/visits/transcript-style";
import { useVisitReads } from "@/hooks/visits/VisitReads";
import type { FileData } from "@/services/files";
import { FileLatestVersion, VisitVersionSelect } from "./VisitVersionSelect";

interface Props {
  file: FileData;
  id: string;
  expanded: boolean;
  onToggle: () => void;
  // The workspace shows the version picker in the note toolbar instead.
  showVersionSelect?: boolean;
}

export const CardContentMainTranscription: React.FC<Props> = ({
  showVersionSelect = true,
  ...props
}) => {
  const v3 = useTranscriptV3();
  const {
    fileVersion,
    versionLoading: loading,
    versionError,
    selectedVersion,
  } = useVisitReads();

  const transcription =
    selectedVersion === FileLatestVersion
      ? props.file.transcription
      : fileVersion?.transcription;

  return (
    <CustomCollapsibleItem
      expanded={props.expanded}
      id={props.id}
      title={
        v3 ? (
          <h3 className="font-v3 text-[13px] font-semibold uppercase tracking-[0.04em] text-v3-text-3">
            {t("visit.recording")} 1
          </h3>
        ) : (
          <h3 className="text-lg font-bold">{t("visit.recording")} #1</h3>
        )
      }
      content={
        <>
          <OwnedAudioPlayer source={props.file.name} />
          {showVersionSelect && props.file.versions.length > 0 && (
            <div className="flex items-center">
              <div className="pb-4">
                <VisitVersionSelect file={props.file} />
              </div>
            </div>
          )}
          {loading || props.file.status === StatusFile.PROCESSING.toString() ? (
            <ParagraphSkeleton lines={10} />
          ) : versionError ? (
            <p role="alert">{t("error.unknown")}</p>
          ) : (
            <SpeakerTranscript
              editable={selectedVersion === FileLatestVersion}
              recordingPublicId={null}
              text={transcription}
            />
          )}
        </>
      }
      onToggle={props.onToggle}
    />
  );
};
